import { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ChatbotWidget } from "../components/ChatbotWidget";
import { useToggle } from "../contexts/ToggleContext";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ContactPage() {
    const enabled = useToggle("contact_form");
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    if (!enabled) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/contact", form);
            setSent(true);
            toast.success("Message received — we'll write back within a working day.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not send. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-ivory" data-testid="contact-page">
            <Header />
            <main className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12">
                <span className="label-eyebrow">Atelier</span>
                <h1 className="mt-3 font-display text-4xl sm:text-5xl text-inkDark tracking-tight">Write to us</h1>
                <p className="mt-2 text-inkMuted max-w-xl">For sizing, customisation, gifting or anything in between &mdash; our atelier reads every note.</p>

                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5 space-y-5">
                        <div className="bg-beige rounded-2xl p-6">
                            <div className="label-eyebrow">Email</div>
                            <div className="mt-1 text-inkDark">hello@sukaanyas.com</div>
                        </div>
                        <div className="bg-beige rounded-2xl p-6">
                            <div className="label-eyebrow">Atelier</div>
                            <div className="mt-1 text-inkDark">Mumbai, India</div>
                            <div className="text-xs text-inkMuted mt-1">Visits by appointment.</div>
                        </div>
                        <div className="bg-beige rounded-2xl p-6">
                            <div className="label-eyebrow">Quick help</div>
                            <div className="mt-1 text-sm text-inkDark">Most questions are answered by our chatbot &mdash; bottom right.</div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        {sent ? (
                            <div className="bg-skyblue/30 border border-skyblueDeep/30 rounded-2xl p-8" data-testid="contact-success">
                                <h2 className="font-display text-2xl text-inkDark">Thank you</h2>
                                <p className="mt-2 text-inkMuted">We&rsquo;ve received your note and will reply within one working day.</p>
                                <button onClick={() => setSent(false)} className="mt-5 btn-ghost text-sm" data-testid="contact-send-another">Send another</button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="bg-ivory border border-inkDark/5 rounded-2xl p-6 lg:p-8 space-y-4" data-testid="contact-form">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Name">
                                        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="contact-name-input" />
                                    </Field>
                                    <Field label="Email">
                                        <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="contact-email-input" />
                                    </Field>
                                </div>
                                <Field label="Subject">
                                    <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="contact-subject-input" />
                                </Field>
                                <Field label="Message">
                                    <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="contact-message-input" />
                                </Field>
                                <div className="flex justify-end">
                                    <button type="submit" disabled={busy} className="btn-pill text-sm disabled:opacity-50" data-testid="contact-submit-button">
                                        {busy ? "Sending…" : "Send message"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
            <ChatbotWidget />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-eyebrow">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
